import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { useMemo } from 'react'
import { Address } from 'viem'
import { useReadContracts } from 'wagmi'

export interface UseEVaultStrategiesParams {
  vaultAddress: CheckedAddress
}

export interface StrategyInfo {
  address: Address
  isInSupplyQueue: boolean
  isInWithdrawQueue: boolean
}

export interface UseEVaultStrategiesResult {
  supplyQueue: Address[]
  withdrawQueue: Address[]
  allStrategies: StrategyInfo[]
  isPending: boolean
}

export function useEVaultStrategies({ vaultAddress }: UseEVaultStrategiesParams): UseEVaultStrategiesResult {
  const queueLengthContracts = useMemo(
    () => [
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'supplyQueueLength' as const,
        chainId: E_EARN_VAULT_CHAIN_ID,
      },
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'withdrawQueueLength' as const,
        chainId: E_EARN_VAULT_CHAIN_ID,
      },
    ],
    [vaultAddress],
  )

  // Step 1: Get queue lengths
  const { data: lengthsData, isPending: isLengthsPending } = useReadContracts({
    contracts: queueLengthContracts,
    query: {
      staleTime: 30000,
    },
  })

  const supplyQueueLength = lengthsData?.[0]?.result as bigint | undefined
  const withdrawQueueLength = lengthsData?.[1]?.result as bigint | undefined

  // Step 2: Get all strategy addresses from both queues
  const supplyQueueContracts = useMemo(
    () =>
      supplyQueueLength
        ? Array.from({ length: Number(supplyQueueLength) }, (_, i) => ({
            address: vaultAddress as Address,
            abi: eEarnVaultImplementationAbi,
            functionName: 'supplyQueue' as const,
            args: [BigInt(i)],
            chainId: E_EARN_VAULT_CHAIN_ID,
          }))
        : [],
    [supplyQueueLength, vaultAddress],
  )

  const withdrawQueueContracts = useMemo(
    () =>
      withdrawQueueLength
        ? Array.from({ length: Number(withdrawQueueLength) }, (_, i) => ({
            address: vaultAddress as Address,
            abi: eEarnVaultImplementationAbi,
            functionName: 'withdrawQueue' as const,
            args: [BigInt(i)],
            chainId: E_EARN_VAULT_CHAIN_ID,
          }))
        : [],
    [vaultAddress, withdrawQueueLength],
  )

  const hasStrategies =
    (supplyQueueLength !== undefined || withdrawQueueLength !== undefined) &&
    (Number(supplyQueueLength || 0) > 0 || Number(withdrawQueueLength || 0) > 0)

  const strategyContracts = useMemo(
    () => [...supplyQueueContracts, ...withdrawQueueContracts],
    [supplyQueueContracts, withdrawQueueContracts],
  )

  const { data: strategiesData, isPending: isStrategiesPending } = useReadContracts({
    contracts: strategyContracts as any,
    query: {
      enabled: hasStrategies,
      staleTime: 30000,
    },
  })

  // Process results
  const { supplyQueue, withdrawQueue, allStrategies } = useMemo(() => {
    const supplyQueue: Address[] = []
    const withdrawQueue: Address[] = []
    const strategyMap = new Map<Address, StrategyInfo>()

    if (strategiesData && supplyQueueLength !== undefined && withdrawQueueLength !== undefined) {
      const supplyCount = Number(supplyQueueLength)
      const withdrawCount = Number(withdrawQueueLength)

      // Process supply queue
      for (let i = 0; i < supplyCount; i++) {
        const result = strategiesData[i]?.result as Address | undefined
        if (result) {
          supplyQueue.push(result)
          const existing = strategyMap.get(result)
          if (existing) {
            existing.isInSupplyQueue = true
          } else {
            strategyMap.set(result, {
              address: result,
              isInSupplyQueue: true,
              isInWithdrawQueue: false,
            })
          }
        }
      }

      // Process withdraw queue
      for (let i = 0; i < withdrawCount; i++) {
        const result = strategiesData[supplyCount + i]?.result as Address | undefined
        if (result) {
          withdrawQueue.push(result)
          const existing = strategyMap.get(result)
          if (existing) {
            existing.isInWithdrawQueue = true
          } else {
            strategyMap.set(result, {
              address: result,
              isInSupplyQueue: false,
              isInWithdrawQueue: true,
            })
          }
        }
      }
    }

    return {
      supplyQueue,
      withdrawQueue,
      allStrategies: Array.from(strategyMap.values()),
    }
  }, [strategiesData, supplyQueueLength, withdrawQueueLength])

  return {
    supplyQueue,
    withdrawQueue,
    allStrategies,
    // Only check isStrategiesPending if the query is enabled (i.e., there are strategies)
    // Otherwise, we're done loading once we have the queue lengths
    isPending: isLengthsPending || (hasStrategies && isStrategiesPending),
  }
}
